// Même principe que l'aide des champs : reconstruire uniquement le HTML autorisé.
const allowedTags = new Set(['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'sub', 'sup', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code', 'a', 'table', 'caption', 'colgroup', 'col', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'span', 'div', 'hr', 'img'])
const discardedTags = new Set(['script', 'style', 'iframe', 'object', 'embed', 'svg', 'math', 'template', 'noscript', 'textarea', 'title', 'xmp', 'plaintext'])
const voidTags = new Set(['br', 'hr', 'img', 'col'])
const size = /^(?:0|\d+(?:\.\d+)?(?:px|pt|em|rem|%))$/i
const color = /^(?:#[a-f\d]{3,8}|[a-z]+|(?:rgb|hsl)a?\([\d.,%\s]+\))$/i
const border = /^(?:\d+(?:\.\d+)?(?:px|pt|em|rem)\s+)?(?:none|solid|dashed|dotted|double)(?:\s+(?:#[a-f\d]{3,8}|[a-z]+|(?:rgb|hsl)a?\([\d.,%\s]+\)))?$/i

function escapeHtml(value) {
  return String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}

function safeUrl(value, image = false) {
  const url = value.trim()
  if (!url || Array.from(url).some(character => character.charCodeAt(0) <= 32 || character.charCodeAt(0) === 127) || url.startsWith('//')) return ''
  if (image && /^data:image\/(?:png|jpe?g|gif|webp);base64,[a-z\d+/=]+$/i.test(url)) return url
  try {
    const protocol = new URL(url, 'https://visual.invalid/').protocol
    return (image ? ['https:', 'http:'] : ['https:', 'http:', 'mailto:', 'tel:']).includes(protocol) ? url : ''
  } catch (error) {
    return ''
  }
}

// Les propriétés de mise en page globale, URL CSS et comportements sont exclus.
function safeStyle(element) {
  return Array.from(element.style).map(property => {
    const value = element.style.getPropertyValue(property).trim()
    let valid = false
    if (['color', 'background-color'].includes(property) || /^border(?:-(?:top|right|bottom|left))?-color$/.test(property)) valid = color.test(value)
    if (['width', 'height', 'max-width', 'min-width', 'font-size', 'border-spacing'].includes(property) || /^border(?:-(?:top|right|bottom|left))?-width$/.test(property)) valid = value.split(/\s+/).every(part => size.test(part))
    if (/^(?:padding|margin)(?:-(?:top|right|bottom|left))?$/.test(property)) valid = value.split(/\s+/).every(part => size.test(part))
    if (/^border(?:-(?:top|right|bottom|left))?$/.test(property)) valid = border.test(value)
    if (/^border(?:-(?:top|right|bottom|left))?-style$/.test(property)) valid = /^(none|solid|dashed|dotted|double)$/.test(value)
    if (property === 'border-collapse') valid = /^(collapse|separate)$/.test(value)
    if (property === 'font-family') valid = /^[a-z\d ,"'-]+$/i.test(value)
    if (property === 'font-weight') valid = /^(normal|bold|bolder|lighter|[1-9]00)$/.test(value)
    if (property === 'font-style') valid = /^(normal|italic|oblique)$/.test(value)
    if (property === 'text-align') valid = /^(left|right|center|justify|start|end)$/.test(value)
    if (property === 'text-decoration') valid = /^(none|underline|line-through|overline)( (underline|line-through|overline))*$/.test(value)
    if (property === 'vertical-align') valid = /^(baseline|sub|super|middle|top|bottom|text-top|text-bottom)$/.test(value)
    if (property === 'white-space') valid = /^(normal|pre|pre-wrap|pre-line|nowrap|break-spaces)$/.test(value)
    return valid ? `${property}: ${value}` : ''
  }).filter(Boolean).join('; ')
}

// La limite porte sur le texte décodé ; le parcours DOM garde les balises fermées.
function truncateHtml(body, limitCaracter) {
  const limit = Math.floor(Number(limitCaracter))
  if (!Number.isFinite(limit) || limit <= 0) return body.innerHTML
  const text = body.textContent || ''
  const characters = typeof Intl !== 'undefined' && Intl.Segmenter
    ? Array.from(new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text), entry => entry.segment)
    : Array.from(text)
  if (characters.length <= limit) return body.innerHTML
  let remaining = characters.slice(0, limit).join('').length
  let done = false
  function visit(parent) {
    Array.from(parent.childNodes).forEach(node => {
      if (done) { parent.removeChild(node); return }
      if (node.nodeType === 3) {
        const length = node.textContent.length
        if (length >= remaining) {
          node.textContent = node.textContent.slice(0, remaining) + '…'
          done = true
        } else remaining -= length
      } else visit(node)
    })
  }
  visit(body)
  return body.innerHTML
}

export function sanitizeVisualHtml(value, limitCaracter = 0) {
  if (value === null || value === undefined || typeof DOMParser === 'undefined') return ''
  const parsed = new DOMParser().parseFromString(String(value), 'text/html')
  function render(node) {
    if (node.nodeType === 3) return escapeHtml(node.textContent || '')
    if (node.nodeType !== 1) return ''
    const tag = node.tagName.toLowerCase()
    if (discardedTags.has(tag) || (node.namespaceURI && node.namespaceURI !== 'http://www.w3.org/1999/xhtml')) return ''
    const content = Array.from(node.childNodes).map(render).join('')
    if (!allowedTags.has(tag)) return content
    const attributes = []
    const attr = (key, val) => { if (val !== '') attributes.push(`${key}="${escapeHtml(val)}"`) }
    attr('style', safeStyle(node))
    attr('title', node.getAttribute('title') || '')
    if (tag === 'a') {
      const href = safeUrl(node.getAttribute('href') || '')
      if (href) { attr('href', href); attr('target', '_blank'); attr('rel', 'noopener noreferrer') }
    }
    if (tag === 'img') {
      const src = safeUrl(node.getAttribute('src') || '', true)
      if (!src) return ''
      attr('src', src)
      attr('alt', node.getAttribute('alt') || '')
      attr('loading', 'lazy')
    }
    if (['td', 'th', 'col', 'colgroup'].includes(tag)) {
      for (const key of ['colspan', 'rowspan', 'span']) {
        const number = node.getAttribute(key) || ''
        if (/^[1-9]\d{0,2}$/.test(number)) attr(key, number)
      }
    }
    if (['table', 'td', 'th', 'img', 'col'].includes(tag)) {
      for (const key of ['width', 'height']) {
        const dimension = node.getAttribute(key) || ''
        if (/^\d+(?:\.\d+)?%?$/.test(dimension)) attr(key, dimension)
      }
    }
    return `<${tag}${attributes.length ? ` ${attributes.join(' ')}` : ''}>${voidTags.has(tag) ? '' : content + `</${tag}>`}`
  }
  const html = Array.from(parsed.body.childNodes).map(render).join('')
  const body = new DOMParser().parseFromString(html, 'text/html').body
  return (body.textContent || '').replace(/[\s\u00a0\u200b-\u200d\ufeff]/g, '') || body.querySelector('img,hr') ? truncateHtml(body, limitCaracter) : ''
}
